import { Router } from '@angular/router';
import {
    ApiService,
    AuthService,
    HomeleiteToken,
    InterceptorConfig,
    RequestMetadata
} from '@homeleite/arch';
import { LoginResult } from '@homeleite/common-pages';
import { TenantsRepository } from '../tenant/tenants.service';
import { lastValueFrom, takeWhile, timer } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import { switchMap, tap } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';

export abstract class BaseLoginService {
    protected metadata: RequestMetadata = {
        autoCatch: InterceptorConfig.NO_INTERCEPT
    };

    protected constructor(
        protected api: ApiService,
        protected tenantsRepository: TenantsRepository,
        protected router: Router,
        protected authService: AuthService,
        protected snackbar: MatSnackBar
    ) {}

    /** Associação ativa neste cliente (vazia = padrão do servidor, APCBRH). O servidor valida contra as associações cadastradas. */
    protected activeTenantId(): string {
        try {
            return HomeleiteToken.decode(this.authService.currentToken)?.tenantId?.trim().toUpperCase() ?? '';
        } catch {
            return '';
        }
    }

    public async handleFirstAccess(identificationNumber: string, apcId: string): Promise<string> {
        const tenant = this.activeTenantId();
        const payload = { identificationNumber, apcId, ...(tenant && tenant !== 'GLOBAL' ? { tenant } : {}) };
        const response = await lastValueFrom(
            this.api.root
                .path('/api/account/first-access')
                .postJson<{ key: string }>(payload, { metadata: this.metadata })
        );

        if (!response.key) {
            throw new Error('Chave de primeiro acesso não retornada pelo servidor.');
        }
        return response.key;
    }

    /** A associação vai no token, a partir do usuário. O cliente não escolhe. */
    protected async fetchToken(username: string, password: string): Promise<HomeleiteToken> {
        return lastValueFrom(
            this.api.root.path(`/public/login`).postJson(
                { username, password },
                { metadata: this.metadata }
            )
        );
    }

    protected handleToken(token: string): LoginResult {
        if (token) {
            this.authService.publishLogin(token);
            this.router.navigate(['/']);
            return LoginResult.success();
        }
        return LoginResult.fail();
    }

    async setPasswordAndEmail(key: string, email: string, password: string): Promise<void> {
        try {
            const payload = {
                email,
                password,
                key
            };

            await lastValueFrom(
                this.api.root
                    .path('api/account/set-password-and-email')
                    .postJson<object>(payload, { metadata: this.metadata })
            );
        } catch (err) {
            console.error('Falha ao definir senha e e-mail:', err);
            throw err;
        }
    }

    async authenticateAndInitiateSync(username: string, password: string): Promise<void> {
        const tokenResult = await this.fetchToken(username, password);

        if (!tokenResult?.token) {
            throw new Error('Não foi possível obter token temporário.');
        }

        const authHeaders = new HttpHeaders({ Authorization: `Bearer ${tokenResult.token}` });
        const requestOptions = { headers: authHeaders };

        try {
            void lastValueFrom(
                this.api.root
                    .path('api/syncs/first-access')
                    .getJson<{ status: string; detail?: string }>(requestOptions)
            );

            const finalStatus = await lastValueFrom(
                timer(5000, 20000).pipe(
                    switchMap(() =>
                        this.api.root
                            .path('api/syncs/first-access-feedback')
                            .getJson<{
                                status: string;
                                detail?: string;
                                srgAlive?: number;
                                hlAlive?: number;
                            }>(requestOptions)
                    ),
                    tap((response) =>
                        console.log(
                            `Verificando status: ${response.status}`,
                            response.detail || '',
                            response.srgAlive != null
                                ? `vivos ${response.hlAlive}/${response.srgAlive}`
                                : ''
                        )
                    ),
                    takeWhile((response) => response.status.toUpperCase() === 'PROCESSING', true)
                )
            );

            const status = finalStatus?.status?.toUpperCase();
            if (status === 'ERROR') {
                const detail = finalStatus.detail?.trim();
                this.showSnackbarMessage(
                    detail
                        ? `Sincronização incompleta com o SRG-SCL: ${detail}`
                        : 'Sincronização incompleta com o SRG-SCL. Contate o suporte.'
                );
                throw new Error(detail || 'Mirror SRG-SCL incompleto');
            }
            if (status !== 'SUCCESS') {
                throw new Error('Sincronização não concluiu com sucesso.');
            }
        } catch (err) {
            console.error('A sincronização falhou: ', err);
            const msg = err instanceof Error ? err.message : '';
            if (!msg.includes('Mirror') && !msg.includes('Sincronização incompleta')) {
                this.showSnackbarMessage(
                    'A sincronização falhou. Por favor, entre em contato com o suporte.'
                );
            }
            // Bloqueia seguir no app com base incompleta.
            throw err;
        }
    }

    private showSnackbarMessage(message: string) {
        this.snackbar.open(message, 'OK', {
            duration: 10000,
            panelClass: ['custom-snackbar'],
            verticalPosition: 'top'
        });
    }
}
